import React from 'react';
import { MenuItem, ListSubheader } from '@mui/material';
import { SelectOptionWithCategory } from '../model';
import {
  BaseSelectInputProps,
  useSelectInputStyles,
  createSelectInputChangeHandler,
  SelectInputWrapper,
  sortByLabel,
} from './SelectInputBase';

interface SelectInputWithCategoriesProps extends BaseSelectInputProps {
  choices: SelectOptionWithCategory[];
  defaultChoice?: { label: string; value: string };
}

const SelectInputWithCategories: React.FC<SelectInputWithCategoriesProps> = (props) => {
  const {
    label,
    onChange,
    valueChanged,
    styleOverrides,
    errorMessage,
    value = '',
    choices,
    defaultChoice,
    disabled = false,
    disableAlphabeticalSort = false,
  } = props;

  const classes = useSelectInputStyles();
  const handleChange = createSelectInputChangeHandler(onChange, valueChanged);

  const createMenuItem = (choice: { label: string; value: string }, indented: boolean = false) =>
    choice ? (
      <MenuItem value={choice.value} key={choice.value} sx={indented ? { pl: 4 } : {}}>
        <span>{choice.label}</span>
      </MenuItem>
    ) : null;

  const defaultMenuItem = createMenuItem(defaultChoice);

  const rawCategories = Array.from(new Set(choices.map((choice) => choice.category)));
  const categories = disableAlphabeticalSort ? rawCategories : [...rawCategories].sort((a, b) => a.localeCompare(b));

  const menuItemChoices = categories.flatMap((category) => {
    const categoryOptions = choices.filter((choice) => choice.category === category);
    const sortedOptions = disableAlphabeticalSort ? categoryOptions : sortByLabel(categoryOptions);
    return [
      <ListSubheader
        key={`header-${category}`}
        disableSticky
        sx={{
          fontWeight: 'bold',
          fontSize: '0.875rem',
          lineHeight: 1.75,
          color: 'text.primary',
          pt: 1,
          pb: 0.5,
        }}
      >
        {category}
      </ListSubheader>,
      ...sortedOptions.map((choice) => createMenuItem(choice, true)),
    ];
  });

  return (
    <SelectInputWrapper
      label={label}
      value={value}
      errorMessage={errorMessage}
      styleOverrides={styleOverrides}
      disabled={disabled}
      classes={classes}
      onChange={handleChange}
    >
      {defaultMenuItem}
      {menuItemChoices}
    </SelectInputWrapper>
  );
};

export default SelectInputWithCategories;
